import type { QueryClient } from '@tanstack/react-query'
import { createRouter } from '@tanstack/react-router'

import { PageSkeleton } from '@ems/ui/patterns/page-skeleton'

import { ErrorView } from '#admin/components/error-view.tsx'
import { NotFoundView } from '#admin/components/not-found-view.tsx'
import { createQueryClient } from '#admin/lib/query-client.ts'
import { routeTree } from './routeTree.gen'

/**
 * The router, and the context every route can rely on.
 *
 * The `QueryClient` goes in the router context so a route **loader** can prefetch with the
 * same options the component later reads — one key, one fetcher, no second request. Without
 * it a loader would either duplicate the query or skip prefetching, and the second choice is
 * how a list ends up fetching after its own skeleton has rendered.
 */
export type RouterContext = {
  queryClient: QueryClient
}

export function createAppRouter() {
  const queryClient = createQueryClient()

  return createRouter({
    routeTree,
    context: { queryClient },
    // The console is behind a login and has no SEO to lose, so a brief pending state beats a
    // flash of skeleton on every navigation.
    defaultPendingMs: 300,
    /**
     * There was none at all: `defaultPendingMs` was set and nothing consumed it, so a slow loader
     * left the *previous* page on screen and fully interactive for as long as the next one took —
     * which invites somebody to click something on a page that is already going away.
     */
    defaultPendingComponent: PageSkeleton,
    defaultPreload: 'intent',
    /**
     * **Errors and "not here" render in the route that failed, not at the root.**
     *
     * With only the root route holding a boundary, a page whose loader was refused replaced the
     * entire console — sidebar and all — with a line of text and no way out. As the default, an
     * error renders in the route that threw, inside its parent's outlet, so the frame and its
     * navigation survive and the state itself offers the way back.
     *
     * A thrown `notFound()` is different: it goes to the nearest route that *declares* a
     * `notFoundComponent`, which renders it in place of itself — the default does not count. So the
     * guarded pages declare their own, and the layout deliberately does not.
     */
    defaultErrorComponent: ({ error, reset }) => <ErrorView error={error} reset={reset} />,
    defaultNotFoundComponent: NotFoundView,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
